import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
  useMemo,
} from "react";
import {
  Row,
  Button,
  Grid,
  Card,
  AuthContext,
  ViewContext,
  Loader,
  useAPI,
} from "components/lib";
import { useParams, useNavigate } from "react-router-dom";
import Axios from "axios";
import MaterialReactTable from "material-react-table";
import { ReadOnlySurveyResponse } from "components/survey/read-only-response";
import {
  parseMeta,
  entityTypeLabel,
  v1RoleLabel,
  relationshipRoleLabel,
  edgeSkipReasonText,
  entityStatusBadge,
  xpmGroupStatusColor,
  edgeSyncBadge,
  v1FlagBadges,
  isV2Group,
  xpmApprovalStatus,
  isAwaitingApproval,
  v2RoleBadges,
  v2Badges,
  tfnStatus,
  approvalPanelVisible,
  liveUnsyncedRoles,
  partyControls,
  splitSkippedRoles,
  previewSections,
  relationshipRoleText,
  unsyncedReasonText,
  xpmMemberCount,
  storedV2Warnings,
  archivePromptText,
} from "./client-group-v2-display";

const EMPTY_V2_PREVIEW = { data: null, loading: false, error: null };

/*
 * The approval panel of a v2 group while XPM sync waits for staff (R21): what
 * XPM would get (GET /v2/preview, read again after each party edit), and
 * Approve and Reject. The party edits sit on each row of the Entities table,
 * so the email control can stay there after approval.
 */
function V2ApprovalPanel({ state, busy, onApprove, onReject }) {
  const sections = previewSections(state.data?.preview, state.data?.errors);
  const lists = [
    { key: "clients", title: "XPM clients", items: sections.clients },
    { key: "contacts", title: "XPM contacts", items: sections.contacts },
    {
      key: "relationships",
      title: "Relationships",
      items: sections.relationships,
      // The sync job writes no relationship while this toggle is off
      // (shouldSyncClientGroupRelationships), so the panel says so.
      note: sections.relationshipSync
        ? null
        : 'Not sent to XPM: "Add client relationships" is off in the Client Onboarding v2 settings.',
    },
    {
      key: "relationshipsSkipped",
      title: "Relationships not sent",
      items: sections.relationshipsSkipped,
    },
    { key: "excluded", title: "Not sent to XPM", items: sections.excluded },
    { key: "unsyncedRoles", title: "Unsynced roles", items: sections.unsyncedRoles },
  ];

  return (
    <Card>
      <div className="flex justify-between items-start gap-4">
        <div>
          <h2 className="text-lg font-semibold">XPM approval</h2>
          <p className="text-gray-500 text-sm mt-1">
            XPM sync waits for staff approval. Check what XPM will get, change a
            party in the Entities table if needed, then approve or reject.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            small
            text="Approve XPM sync"
            loading={busy === "approve"}
            action={onApprove}
          />
          <Button
            small
            color="red"
            text="Reject"
            loading={busy === "reject"}
            action={onReject}
          />
        </div>
      </div>

      {state.loading && !state.data && <Loader />}
      {state.error && (
        <div className="mt-4 rounded border border-sg-red-bg bg-sg-red-bg p-4 text-sm text-sg-red">
          {state.error}
        </div>
      )}

      {state.data && (
        <div className="mt-4 space-y-4">
          {sections.errors.length > 0 && (
            <div className="rounded border border-sg-red-bg bg-sg-red-bg p-3 text-sm text-sg-red space-y-1">
              {sections.errors.map((error) => (
                <div key={error.key}>{error.text}</div>
              ))}
            </div>
          )}
          {sections.warnings.length > 0 && (
            <div className="rounded bg-sg-orange-bg p-3 text-sm text-sg-orange space-y-1">
              {sections.warnings.map((warning) => (
                <div key={warning.key}>{warning.text}</div>
              ))}
            </div>
          )}
          {!sections.syncEnabled && (
            <p className="text-sm text-gray-600">
              XPM auto-create is off in the Client Onboarding v2 settings, so
              XPM sync cannot be approved until it is turned on.
            </p>
          )}
          <p className="text-sm text-gray-600">
            XPM client group:{" "}
            <span className="font-medium">
              {sections.group && sections.groupSync
                ? sections.group
                : "none will be created"}
            </span>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {lists.map((list) => (
              <div key={list.key}>
                <h3 className="text-sm font-medium text-gray-700 mb-1">
                  {list.title} ({list.items.length})
                </h3>
                {list.note && (
                  <p className="text-xs text-sg-orange mb-1">{list.note}</p>
                )}
                {list.items.length === 0 ? (
                  <p className="text-sm text-gray-500">None</p>
                ) : (
                  <div className="space-y-1">
                    {list.items.map((item) => (
                      <div key={item.key} className="text-sm text-gray-600">
                        <span className="font-medium">{item.text}</span>
                        {item.detail && (
                          <div className="text-xs text-gray-500">
                            {item.detail}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

const CONTROL_SELECT_CLASS =
  "text-xs border border-gray-300 rounded px-1 py-1 bg-white disabled:opacity-50";

// One staff edit control of a v2 party row (partyControls in
// client-group-v2-display.js).
function PartyControl({ entity, control, busy, onEdit, onAddEmail }) {
  const stop = (e) => e.stopPropagation();

  if (control.field === "is_client") {
    return (
      <label className="flex items-center gap-1 text-xs text-gray-600">
        Client
        <select
          value={control.value ? "yes" : "no"}
          disabled={busy}
          onClick={stop}
          onChange={(e) =>
            onEdit(entity.id, { is_client: e.target.value === "yes" })
          }
          className={CONTROL_SELECT_CLASS}
        >
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </label>
    );
  }

  if (control.field === "parent_entity_id") {
    return (
      <label className="flex items-center gap-1 text-xs text-gray-600">
        Parent
        <select
          value={control.value}
          disabled={busy}
          onClick={stop}
          onChange={(e) =>
            onEdit(entity.id, { parent_entity_id: e.target.value })
          }
          className={CONTROL_SELECT_CLASS}
        >
          {!control.value && <option value="">Choose</option>}
          {control.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (control.field === "email") {
          onAddEmail(entity);
          return;
        }
        // Excluding a party also destroys its held tax number (KTD13 rule 6).
        if (
          control.value === true &&
          !window.confirm(
            `Exclude ${entity.name || "this party"} from XPM? A held tax number for it is destroyed.`
          )
        ) {
          return;
        }
        onEdit(entity.id, { exclude: control.value });
      }}
      disabled={busy}
      className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
    >
      {control.label}
    </button>
  );
}

export function ClientGroupDetailView() {
  const params = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);
  const [groupRefreshCount, setGroupRefreshCount] = useState(0);
  const groupAPI = useAPI(
    `/api/client-groups/${params.id}`,
    "get",
    groupRefreshCount
  );
  const [actionLoading, setActionLoading] = useState({});
  const [activeTab, setActiveTab] = useState("entities");
  const [submission, setSubmission] = useState({
    data: null,
    loading: false,
    loaded: false,
    error: null,
  });
  const [v2Preview, setV2Preview] = useState(EMPTY_V2_PREVIEW);
  const [approvalBusy, setApprovalBusy] = useState(null);
  // The group on screen now. A v2 request that answers after staff moved to
  // another group (the instance is reused) must not touch that group's screen.
  const currentIdRef = useRef(params.id);
  currentIdRef.current = params.id;

  const group = groupAPI?.data;
  const entities = useMemo(() => group?.entities || [], [group]);
  const v2 = isV2Group(group);
  const approvalStatus = xpmApprovalStatus(group);
  // While pending, the preview is current and the stored lists are not.
  const liveSkipped = liveUnsyncedRoles(group, v2Preview.data);
  const serverEditableFields = v2Preview.data?.editable_fields;

  const refreshGroup = useCallback(() => {
    setGroupRefreshCount((count) => count + 1);
  }, []);

  useEffect(() => {
    setActiveTab("entities");
    setSubmission({
      data: null,
      loading: false,
      loaded: false,
      error: null,
    });
    // The instance is reused across /client-groups/:id navigations, so the
    // previous group's preview must not stay on screen.
    setV2Preview(EMPTY_V2_PREVIEW);
    setApprovalBusy(null);
  }, [params.id]);

  // The XPM preview of a v2 group awaiting approval, read again each time the
  // group is (after an edit, an approval or a sync).
  useEffect(() => {
    if (!approvalPanelVisible(group)) return undefined;

    let cancelled = false;
    setV2Preview((prev) => ({ ...prev, loading: true, error: null }));

    Axios.get(`/api/client-groups/${params.id}/v2/preview`)
      .then((res) => {
        if (cancelled) return;
        setV2Preview({ data: res.data?.data || null, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setV2Preview({
          data: null,
          loading: false,
          error: err.response?.data?.message || "Failed to load the XPM preview",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [group, params.id]);

  useEffect(() => {
    if (activeTab !== "submission" || submission.loaded) {
      return undefined;
    }

    let cancelled = false;
    setSubmission((prev) => ({ ...prev, loading: true, error: null }));

    Axios.get(`/api/client-groups/${params.id}/submission`)
      .then((res) => {
        if (cancelled) return;
        setSubmission({
          data: res.data?.data || null,
          loading: false,
          loaded: true,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setSubmission({
          data: null,
          loading: false,
          loaded: true,
          error:
            err.response?.data?.message || "Failed to load the submission",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, params.id, submission.loaded]);

  const triggerVerification = useCallback(async (entityId) => {
    setActionLoading((prev) => ({ ...prev, [entityId]: "verify" }));
    try {
      const res = await Axios.post(
        `/api/client-groups/${params.id}/entities/${entityId}/verify`
      );
      viewContext.notification.show(
        res.data.message || "Verification triggered",
        "success",
        true
      );
      refreshGroup();
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Failed to trigger verification",
        "error",
        true
      );
    } finally {
      setActionLoading((prev) => ({ ...prev, [entityId]: null }));
    }
  }, [params.id, refreshGroup, viewContext]);

  // Group-level: create/populate the XPM client group and write its
  // relationships. Safe to re-run — the job resumes from what already synced
  // rather than replaying it.
  const triggerGroupSync = useCallback(async () => {
    try {
      const res = await Axios.post(
        `/api/client-groups/${params.id}/pms-group-sync`
      );
      viewContext.notification.show(
        res.data.message || "Client group sync queued",
        "success",
        true
      );
      refreshGroup();
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Failed to queue client group sync",
        "error",
        true
      );
    }
  }, [params.id, refreshGroup, viewContext]);

  /*
   * Write a tax number onto an entity whose XPM client already exists.
   *
   * The automatic write runs last, deliberately, so the failure it isolates --
   * client created, tax number refused -- always leaves a SYNCED entity behind,
   * and "Sync to PMS" refuses those ("Entity already synced"). Without this the
   * only recovery was a hand-crafted API call.
   *
   * The number is pasted from the one-time secret the firm was mailed at
   * submission; the modal POSTs it straight to the route and nothing stores it
   * here.
   */
  const writeTaxNumber = useCallback(
    (entityId) => {
      viewContext.modal.show(
        {
          title: "Write tax number to XPM",
          text: "Paste the tax number from the secure link you were emailed when this client group was submitted. It is sent straight to XPM and is not stored here.",
          form: {
            tfn: {
              label: "Tax number",
              type: "text",
              required: true,
              errorMessage: "Please enter the tax number",
            },
          },
          buttonText: "Write to XPM",
          url: `/api/client-groups/${params.id}/entities/${entityId}/xpm-tfn`,
          method: "POST",
        },
        () => refreshGroup()
      );
    },
    [params.id, refreshGroup, viewContext]
  );

  const triggerSync = useCallback(async (entityId) => {
    setActionLoading((prev) => ({ ...prev, [entityId]: "sync" }));
    try {
      const res = await Axios.post(
        `/api/client-groups/${params.id}/entities/${entityId}/sync`
      );
      viewContext.notification.show(
        res.data.message || "Sync job queued",
        "success",
        true
      );
      refreshGroup();
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Failed to trigger sync",
        "error",
        true
      );
    } finally {
      setActionLoading((prev) => ({ ...prev, [entityId]: null }));
    }
  }, [params.id, refreshGroup, viewContext]);

  /*
   * A staff edit of one v2 party (R21): any of { is_client, parent_entity_id,
   * exclude, email }. The server runs the rules again and answers with the new
   * preview. A locked field (409) or a parent the person holds no role in (400)
   * comes back as a message.
   */
  const editParty = useCallback(
    async (entityId, body) => {
      const groupId = params.id;
      setActionLoading((prev) => ({ ...prev, [entityId]: "edit" }));
      try {
        const res = await Axios.patch(
          `/api/client-groups/${groupId}/v2/parties/${entityId}`,
          body
        );
        if (currentIdRef.current !== groupId) return;
        if (res.data?.data) {
          setV2Preview({ data: res.data.data, loading: false, error: null });
        }
        viewContext.notification.show(
          res.data.message || "Party updated",
          "success",
          true
        );
        refreshGroup();
      } catch (err) {
        if (currentIdRef.current !== groupId) return;
        viewContext.notification.show(
          err.response?.data?.message || "Failed to update the party",
          "error",
          true
        );
      } finally {
        setActionLoading((prev) => ({ ...prev, [entityId]: null }));
      }
    },
    [params.id, refreshGroup, viewContext]
  );

  const addEmail = useCallback(
    (entity) => {
      const email = window.prompt(
        `Email address for ${entity.name || "this person"}`
      );
      if (email === null || !email.trim()) return;
      editParty(entity.id, { email: email.trim() });
    },
    [editParty]
  );

  const approveXpmSync = useCallback(async () => {
    if (approvalBusy) return;
    if (
      !window.confirm(
        "Approve XPM sync for this client group? The XPM records are created now."
      )
    ) {
      return;
    }
    const groupId = params.id;
    setApprovalBusy("approve");
    try {
      const res = await Axios.post(`/api/client-groups/${groupId}/v2/approve`);
      if (currentIdRef.current !== groupId) return;
      viewContext.notification.show(
        res.data.message || "XPM sync approved",
        "success",
        true
      );
      refreshGroup();
    } catch (err) {
      if (currentIdRef.current !== groupId) return;
      viewContext.notification.show(
        err.response?.data?.message || "Failed to approve XPM sync",
        "error",
        true
      );
    } finally {
      // Another group's approve may be in flight by now.
      if (currentIdRef.current === groupId) setApprovalBusy(null);
    }
  }, [approvalBusy, params.id, refreshGroup, viewContext]);

  // Reject ends the approval and archives the group, as Archive does.
  const rejectXpmSync = useCallback(async () => {
    if (approvalBusy) return;
    const reason = window.prompt(
      "Reject XPM sync? This archives the client group. Reason (optional)"
    );
    if (reason === null) return;
    const groupId = params.id;
    setApprovalBusy("reject");
    try {
      const res = await Axios.post(
        `/api/client-groups/${groupId}/v2/reject`,
        reason.trim() ? { reason: reason.trim() } : {}
      );
      if (currentIdRef.current !== groupId) return;
      viewContext.notification.show(
        res.data.message || "XPM sync rejected",
        "success",
        true
      );
      refreshGroup();
    } catch (err) {
      if (currentIdRef.current !== groupId) return;
      viewContext.notification.show(
        err.response?.data?.message || "Failed to reject XPM sync",
        "error",
        true
      );
    } finally {
      if (currentIdRef.current === groupId) setApprovalBusy(null);
    }
  }, [approvalBusy, params.id, refreshGroup, viewContext]);

  const updateGroupStatus = async (newStatus, archiveReason = null) => {
    try {
      await Axios.patch(
        `/api/client-groups/${params.id}`,
        archiveReason
          ? { status: newStatus, archive_reason: archiveReason }
          : { status: newStatus }
      );
      viewContext.notification.show("Status updated", "success", true);
      refreshGroup();
    } catch (err) {
      // A v2 group's refusal says what to do next (approve or reject first,
      // KTD10). A v1 group keeps the message it has always shown.
      viewContext.notification.show(
        (v2 && err.response?.data?.message) || "Failed to update status",
        "error",
        true
      );
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "type",
        header: "Type",
        size: 120,
        Cell: ({ cell }) => {
          const type = cell.getValue();
          return (
            <span className="px-2 py-1 rounded bg-sg-blue-tint text-brand-600 text-xs font-medium">
              {entityTypeLabel(type)}
            </span>
          );
        },
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 220,
        Cell: ({ cell, row }) => {
          const meta = parseMeta(row.original.metadata);
          const parent = meta?.parent_entity_id;
          return (
            <span className={parent ? "pl-4 border-l-2 border-sg-blue-line" : ""}>
              {cell.getValue()}
            </span>
          );
        },
      },
      {
        accessorKey: "metadata_role",
        header: "Role",
        size: 200,
        accessorFn: (row) => parseMeta(row.metadata)?.role || "",
        Cell: ({ row }) => {
          const meta = parseMeta(row.original.metadata);
          // A v2 party: one badge for each role it holds, with v2 labels (R22).
          if (v2 && meta?.party_id) {
            const roles = v2RoleBadges(meta);
            if (!roles.length) return null;
            return (
              <div className="text-xs space-y-1">
                {roles.map((role) => (
                  <div key={role.key}>
                    <span className="inline-block px-2 py-0.5 rounded bg-sg-blue-tint text-brand-600 font-medium">
                      {role.text}
                    </span>
                    {role.entityName && (
                      <div className="text-gray-500 mt-0.5">
                        of {role.entityName}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          }
          if (!meta?.role) return null;
          const label = v1RoleLabel(meta.role);
          return (
            <div className="text-xs">
              <span className="inline-block px-2 py-0.5 rounded bg-sg-blue-tint text-brand-600 font-medium">
                {label}
              </span>
              {meta.parent_entity_name && (
                <div className="text-slate-500 mt-0.5">
                  of {meta.parent_entity_name}
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => {
          const badge = entityStatusBadge(cell.getValue());
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}
            >
              {badge.text}
            </span>
          );
        },
      },
      {
        accessorKey: "metadata",
        header: "Flags",
        size: 100,
        Cell: ({ cell, row }) => {
          const metadata = parseMeta(cell.getValue());
          // A v2 party: its markers and its TFN state (R12, R20, KTD13).
          if (v2 && metadata?.party_id) {
            const tfn = tfnStatus(row.original, approvalStatus);
            const flags = [
              ...(v1FlagBadges(metadata) || []),
              ...v2Badges(row.original, group?.metadata, liveSkipped),
              ...(tfn
                ? [{ key: "tfn", text: tfn.text, title: tfn.title, color: tfn.color }]
                : []),
            ];
            if (!flags.length) return null;
            return (
              <div className="flex flex-col items-start gap-1">
                {flags.map((badge) => (
                  <span
                    key={badge.key}
                    className={`px-2 py-1 rounded ${badge.color} text-xs font-medium`}
                    title={badge.title}
                  >
                    {badge.text}
                    {badge.reason && (
                      <span className="block font-normal">{badge.reason}</span>
                    )}
                  </span>
                ))}
              </div>
            );
          }
          const badges = v1FlagBadges(metadata);
          if (!badges) return null;
          return (
            <div className="flex flex-col items-start gap-1">
              {badges.map((badge) => (
                <span
                  key={badge.key}
                  className={`px-2 py-1 rounded ${badge.color} text-xs font-medium`}
                  title={badge.title}
                >
                  {badge.text}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: "external_id",
        header: "PMS",
        size: 100,
        Cell: ({ cell, row }) => {
          const externalId = cell.getValue();
          if (externalId) {
            return (
              <span className="text-xs text-sg-green">
                {row.original.external_source}: {externalId.slice(0, 8)}...
              </span>
            );
          }
          return <span className="text-xs text-slate-400">Not synced</span>;
        },
      },
      {
        accessorKey: "id",
        header: "Actions",
        size: 200,
        Cell: ({ row }) => {
          const entity = row.original;
          const meta = parseMeta(entity.metadata);
          const isDerivedContact = Boolean(meta?.parent_entity_id);
          const loading = actionLoading[entity.id];
          const v2Party = v2 && Boolean(meta?.party_id);
          const verifyButton = entity.type === "individual" &&
            !entity.verification_id && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  triggerVerification(entity.id);
                }}
                disabled={loading === "verify"}
                className="px-2 py-1 text-xs bg-btn-primary text-white rounded hover:bg-btn-primary-hover disabled:opacity-50"
              >
                {loading === "verify" ? "..." : "Verify"}
              </button>
            );
          // An excluded v2 party never reaches XPM, and the sync refuses it.
          const syncButton = !entity.external_id &&
            !isDerivedContact &&
            !(v2Party && meta.pms_exclude) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  triggerSync(entity.id);
                }}
                disabled={loading === "sync"}
                className="px-2 py-1 text-xs bg-slate-600 text-white rounded hover:bg-slate-700 disabled:opacity-50"
              >
                {loading === "sync" ? "..." : "Sync to PMS"}
              </button>
            );

          if (v2Party) {
            const tfn = tfnStatus(entity, approvalStatus);
            return (
              <div className="flex flex-wrap gap-2">
                {verifyButton}
                {syncButton}
                {/* Admin-only, matching the route, as in v1. The label says
                    why the held number did not reach XPM (KTD13 rule 8). */}
                {authContext.permission?.admin && tfn?.manualWrite && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      writeTaxNumber(entity.id);
                    }}
                    title={tfn.title}
                    className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    {tfn.manualWriteLabel}
                  </button>
                )}
                {partyControls(
                  group,
                  entity,
                  entities,
                  serverEditableFields
                ).map((control) => (
                  <PartyControl
                    key={control.field}
                    entity={entity}
                    control={control}
                    busy={loading === "edit"}
                    onEdit={editParty}
                    onAddEmail={addEmail}
                  />
                ))}
              </div>
            );
          }

          return (
            <div className="flex gap-2">
              {verifyButton}
              {syncButton}
              {/* Admin-only, matching the route. Offering it to a role the API
                  refuses would turn a recovery affordance into a 403. */}
              {authContext.permission?.admin &&
                meta?.tfn_sync_status === "failed" &&
                entity.external_id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    writeTaxNumber(entity.id);
                  }}
                  title={meta.tfn_sync_error || "The tax number was not written"}
                  className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Retry tax number
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [
      actionLoading,
      addEmail,
      approvalStatus,
      authContext.permission,
      editParty,
      entities,
      group,
      liveSkipped,
      serverEditableFields,
      triggerSync,
      triggerVerification,
      v2,
      writeTaxNumber,
    ]
  );

  if (groupAPI.loading) return <Loader />;
  if (!group)
    return (
      <Grid>
        <p>Client group not found.</p>
      </Grid>
    );

  const metadata =
    typeof group.metadata === "string"
      ? JSON.parse(group.metadata)
      : group.metadata;

  const relationshipEdges = metadata?.relationship_edges || [];
  const relationshipEdgesSkipped = metadata?.relationship_edges_skipped || [];
  // A v2 group lists its unsynced roles, and its duplicates (one copy sent) apart.
  const v2Skipped = v2 ? splitSkippedRoles(relationshipEdgesSkipped) : null;
  const skippedListed = v2 ? v2Skipped.unsynced : relationshipEdgesSkipped;
  const xpmGroup = metadata?.xpm_group || null;
  const xpmRelationshipEdges = metadata?.xpm_relationships?.edges || {};

  // Pre-resolution groups only have relationships denormalised onto each entity,
  // which stores every edge twice — once per side. De-duplicate on the way out.
  const legacyRelationships = Object.values(
    entities.reduce((acc, entity) => {
      (parseMeta(entity.metadata)?.relationships || []).forEach((rel) => {
        acc[`${rel.from_entity}|${rel.relationship_role}|${rel.to_entity}`] = rel;
      });
      return acc;
    }, {})
  );

  // One skipped relationship row of the relationships card.
  const skippedLine = (skip, idx) => (
    <div key={idx} className="text-sm text-gray-500">
      <span className="font-medium">
        {String(skip.row?.from_entity ?? "") || "—"}
      </span>{" "}
      {v2
        ? relationshipRoleText(
            true,
            skip.role || skip.row?.relationship_role,
            skip.role_other
          )
        : relationshipRoleLabel(skip.row?.relationship_role)}{" "}
      <span className="font-medium">
        {String(skip.row?.to_entity ?? "") || "—"}
      </span>
      {" — "}
      {v2 ? unsyncedReasonText(skip.reason) : edgeSkipReasonText(skip.reason)}
    </div>
  );

  // Top-level entities are the only ones eligible for the XPM client group;
  // derived individuals are created as contacts under their parent client.
  // A v2 group's excluded parties are not members either.
  const xpmEligibleCount = xpmMemberCount(group, entities);

  // The warnings the submission or approve stored, on a v2 group whose approval
  // panel (which lists the live ones) is hidden. None on a v1 group. The rows
  // drop a no-email warning that a late "Add email" settled.
  const v2Warnings = storedV2Warnings(group, entities);

  return (
    <Grid>
      <Row>
        <button
          onClick={() => navigate("/client-groups")}
          className="text-sm text-sg-blue hover:underline"
        >
          &larr; Back to Client Groups
        </button>
      </Row>

      <Card>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">{group.name}</h1>
            {metadata?.needs_group_name && (
              // The form did not ask the client to name their group, so this is
              // the primary contact's name standing in. Renaming the group clears
              // the flag and is what lets the XPM client group be created.
              <p className="mt-1">
                <span className="px-2 py-1 rounded bg-sg-orange-bg text-sg-orange text-xs font-medium">
                  Needs a name
                </span>
                <span className="text-gray-500 text-sm ml-2">
                  Named after the primary contact. Rename it before syncing to
                  XPM.
                </span>
              </p>
            )}
            <p className="text-slate-500 text-sm mt-1">
              {group.entity_count} entit
              {group.entity_count === 1 ? "y" : "ies"} &middot; Submitted{" "}
              {new Date(group.created_at).toLocaleDateString()}
              {metadata?.submitted_by_email &&
                ` by ${metadata.submitted_by_email}`}
            </p>
            {xpmGroup && (
              <div className="flex items-center gap-2 mt-2 text-sm text-slate-600">
                <span>
                  XPM group:{" "}
                  <span className="font-medium">{xpmGroup.name}</span>
                </span>
                <span
                  className={`px-2 py-1 rounded text-xs ${xpmGroupStatusColor(
                    xpmGroup.status
                  )}`}
                >
                  {xpmGroup.status}
                </span>
                <span className="text-slate-500">
                  {Object.keys(xpmGroup.members || {}).length}/
                  {xpmEligibleCount} members
                </span>
                {xpmGroup.strategy_unverified && (
                  <span
                    title="XPM did not return a group identifier, so remaining members could not be added. Re-run the sync."
                    className="px-2 py-1 rounded text-xs bg-sg-red-bg text-sg-red"
                  >
                    no group id
                  </span>
                )}
                {xpmGroup.waited_out && (
                  <span
                    title={`Synced without ${
                      (xpmGroup.unresolved_entity_ids || []).length
                    } entit${
                      (xpmGroup.unresolved_entity_ids || []).length === 1
                        ? "y"
                        : "ies"
                    } that never got an XPM client.`}
                    className="px-2 py-1 rounded text-xs bg-sg-orange-bg text-sg-orange"
                  >
                    incomplete
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              small
              color="blue"
              text="Sync to XPM group"
              action={triggerGroupSync}
            />
            {group.status === "submitted" && (
              <Button
                small
                text="Start Review"
                action={() => updateGroupStatus("in_review")}
              />
            )}
            {/* A v2 group waiting for XPM approval cannot complete (KTD10). */}
            {group.status === "in_review" && !isAwaitingApproval(group) && (
              <Button
                small
                text="Mark Complete"
                action={() => updateGroupStatus("complete")}
              />
            )}
            {group.status !== "archived" && (
              <Button
                small
                color="red"
                text="Archive"
                action={() => {
                  // On a v2 group, archiving destroys any held tax numbers, and
                  // while the approval is pending it also rejects XPM sync, so
                  // the prompt says so.
                  const reason = window.prompt(archivePromptText(group, entities));
                  if (reason !== null) updateGroupStatus("archived", reason);
                }}
              />
            )}
          </div>
        </div>
      </Card>

      {v2Warnings.length > 0 && (
        <div className="rounded bg-sg-orange-bg p-3 text-sm text-sg-orange space-y-1">
          {v2Warnings.map((warning) => (
            <div key={warning.key}>{warning.text}</div>
          ))}
        </div>
      )}

      {approvalPanelVisible(group) && (
        <V2ApprovalPanel
          state={v2Preview}
          busy={approvalBusy}
          onApprove={approveXpmSync}
          onReject={rejectXpmSync}
        />
      )}

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex gap-6" aria-label="Client group sections">
          {[
            { key: "entities", label: "Entities" },
            { key: "submission", label: "Submission" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
                activeTab === tab.key
                  ? "border-brand-500 text-sg-blue"
                  : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "entities" && (
        <>
          <Card>
            <h2 className="text-lg font-semibold mb-4">Entities</h2>
            <MaterialReactTable
              columns={columns}
              data={entities}
              enableColumnActions={false}
              enableColumnFilters={false}
              enablePagination={false}
              enableSorting={true}
              enableBottomToolbar={false}
              enableTopToolbar={false}
            />
          </Card>

          {/* Relationships. While a v2 approval is pending, staff edits change
              the parties but not these stored lists (approve rebuilds them), so
              the approval panel's live preview stands in for this card. */}
          {!approvalPanelVisible(group) &&
            (relationshipEdges.length > 0 ||
            relationshipEdgesSkipped.length > 0 ||
            legacyRelationships.length > 0) && (
            <Card>
              <h2 className="text-lg font-semibold mb-4">
                Entity Relationships
              </h2>

              {/* Groups submitted before edges were resolved keep the old
                  per-entity read. It stores each edge on both sides, so
                  de-duplicate rather than rendering everything twice. */}
              {relationshipEdges.length === 0 &&
                legacyRelationships.length > 0 && (
                  <div className="space-y-2">
                    {legacyRelationships.map((rel, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-sm text-slate-600"
                      >
                        <span className="font-medium">{rel.from_entity}</span>
                        <span className="text-sg-blue">
                          {relationshipRoleLabel(rel.relationship_role)}
                        </span>
                        <span className="font-medium">{rel.to_entity}</span>
                      </div>
                    ))}
                  </div>
                )}

              {relationshipEdges.length > 0 && (
                <div className="space-y-2">
                  {relationshipEdges.map((edge) => {
                    const sync = xpmRelationshipEdges[edge.key];
                    const syncBadge = sync && edgeSyncBadge(sync);
                    return (
                      <div
                        key={edge.key}
                        className="flex items-center justify-between gap-3 text-sm text-slate-600"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{edge.from_name}</span>
                          <span className="text-sg-blue">
                            {relationshipRoleText(v2, edge.role, edge.role_other)}
                          </span>
                          <span className="font-medium">{edge.to_name}</span>
                        </div>
                        {syncBadge && (
                          <span
                            title={syncBadge.title}
                            className={`px-2 py-1 rounded text-xs whitespace-nowrap ${syncBadge.color}`}
                          >
                            {syncBadge.text}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {skippedListed.length > 0 && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <h3 className="text-sm font-medium text-slate-700 mb-2">
                    {v2 ? "Unsynced roles" : "Not synced"} ({skippedListed.length})
                  </h3>
                  <div className="space-y-1">
                    {skippedListed.map(skippedLine)}
                  </div>
                </div>
              )}

              {v2 && v2Skipped.duplicates.length > 0 && (
                <div className="mt-4 border-t border-gray-100 pt-3">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Entered more than once ({v2Skipped.duplicates.length})
                  </h3>
                  <p className="text-xs text-gray-500 mb-2">
                    One copy of each is sent to XPM.
                  </p>
                  <div className="space-y-1">
                    {v2Skipped.duplicates.map(skippedLine)}
                  </div>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {activeTab === "submission" && (
        <Card>
          <div className="mb-4">
            <h2 className="text-lg font-semibold">Submission</h2>
            <p className="text-sm text-slate-500">
              Read-only view of the original client onboarding form response.
            </p>
          </div>
          {submission.loading && <Loader />}
          {!submission.loading && submission.error && (
            <div className="rounded border border-sg-red-bg bg-sg-red-bg p-4 text-sm text-sg-red">
              {submission.error}
            </div>
          )}
          {!submission.loading && !submission.error && !submission.data && (
            <div className="rounded border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              No linked submission was found for this client group.
            </div>
          )}
          {!submission.loading && !submission.error && submission.data && (
            <ReadOnlySurveyResponse response={submission.data} />
          )}
        </Card>
      )}
    </Grid>
  );
}

export default ClientGroupDetailView;
